import * as React from "react";
import OutlinedInput, { type OutlinedInputProps } from "@mui/material/OutlinedInput";

import { cn } from "../../lib/utils";

export type TextareaProps = Omit<
  OutlinedInputProps,
  "multiline" | "rows" | "minRows" | "maxRows" | "inputRef" | "inputProps"
> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    containerClassName?: string;
    inputClassName?: string;
    minRows?: number;
    maxRows?: number;
    inputProps?: OutlinedInputProps["inputProps"];
  };

const textareaSx = {
  minHeight: 80,
  borderRadius: "0.375rem",
  backgroundColor: "#fff",
  alignItems: "flex-start",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#CBD5E1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#00BC7D",
    borderWidth: "1px",
  },
  "&.Mui-focused": {
    boxShadow: "0 0 0 1px #00BC7D",
  },
  "&.Mui-disabled": {
    opacity: 0.5,
  },
  "& .MuiInputBase-inputMultiline": {
    minHeight: "80px !important",
    boxSizing: "border-box",
    padding: "10px 12px",
    fontFamily: "Nunito",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    resize: "vertical",
  },
  "& .MuiInputBase-inputMultiline::placeholder": {
    color: "rgba(0, 0, 0, 0.38)",
    opacity: 1,
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
    marginTop: "6px",
  },
  "@media (prefers-color-scheme: dark)": {
    backgroundColor: "#1f2937",
    color: "#f9fafb",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.23)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#00BC7D",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#00BC7D",
      borderWidth: "1px",
    },
    "& .MuiInputBase-inputMultiline::placeholder": {
      color: "rgba(255, 255, 255, 0.5)",
    },
  },
} as const;

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      containerClassName,
      inputClassName,
      onInput,
      cols,
      wrap,
      rows = 3,
      minRows,
      maxRows,
      inputProps,
      ...props
    },
    ref,
  ) => {
    const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
      onInput?.(e);

      e.currentTarget.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    };

    return (
      <OutlinedInput
        {...props}
        fullWidth
        multiline
        rows={rows}
        minRows={minRows ?? rows}
        maxRows={maxRows}
        inputRef={ref}
        className={cn(containerClassName, className)}
        sx={textareaSx}
        inputProps={{
          ...inputProps,
          cols,
          onInput: handleInput,
          wrap,
          className: cn(
            "!text-sm !leading-4",
            inputProps?.className,
            inputClassName,
          ),
        }}
      />
    );
  },
);

Textarea.displayName = "Textarea";

export { Textarea };
